import { useMediaQuery, Box, Typography, Button } from '@mui/material';
import { mobileWidth } from '@/constants';
import {
  BalanceDataInterface,
  AccountCategory,
} from '@/interfaces/balanceSheet';
import { DataRangeFilterEnum } from '@/constants/dates';
import { BalanceSectionHeader } from '@/modules/balanceSheet/components/BalanceSectionHeader';
import { useDrilldown } from '@/hooks/useDrilldown';
import { DrilldownView } from '@/components/views/DrilldownView';
import AccountsGrid from '@/components/grids/AccountsGrid';
import { useLocalization } from '@/hooks/useLocalization';
import PieChartWithLegend from '@/components/common/charts/PieChartWithLegend';
import { useGraphColors } from '@/hooks/useGraphColors';
import { useWidgetWidthIsBelow } from '@/hooks/useWidgetWidthIsBelow';

const GROUPING_THRESHOLD = 2;

const FULL_PIE_RADIUS = 140;
const PIE_EDGE_SPACE = 8;

interface BalanceSheetPieChartProps {
  balanceData: BalanceDataInterface;
  dateFilter: DataRangeFilterEnum;
  /** Stacks the two charts regardless of viewport width, for the half-width
   * widget slot (IU-245). */
  stacked?: boolean;
  /** When provided, a slice click bubbles up instead of drilling inline (IU-31). */
  onDrilldown?: (category: AccountCategory) => void;
  /** Drill-down accounts grid fills the page height. Off inside a widget. */
  fillDrilldownHeight?: boolean;
}

export default function BalanceSheetPieChart({
  balanceData,
  dateFilter: _dateFilter,
  stacked = false,
  onDrilldown,
  fillDrilldownHeight = false,
}: BalanceSheetPieChartProps) {
  const isMobileMatch = useMediaQuery(mobileWidth);
  // Both sections share one width, so measuring the first sizes both pies. A
  // fixed size is wider than a phone-width or minimum-width dashboard card. The
  // sections stretch to the card rather than to their pie, or the measurement
  // would only ever report the pie's own width back.
  const { ref: sectionRef, widthPx: sectionWidth } = useWidgetWidthIsBelow(
    true,
    0,
  );
  const fullPieWidth = isMobileMatch ? 300 : 350;
  const pieWidth =
    sectionWidth === null
      ? fullPieWidth
      : Math.min(fullPieWidth, Math.floor(sectionWidth));
  const pieHeight = pieWidth < fullPieWidth ? pieWidth : 350;
  // The pie draws at a fixed radius regardless of its box, so a smaller box
  // needs a smaller radius or the slices are cut off at the edges.
  const pieRadius = Math.min(
    FULL_PIE_RADIUS,
    Math.floor(pieWidth / 2) - PIE_EDGE_SPACE,
  );
  const { assets, liabilities } = balanceData;
  const { drilledDownCategory, handleDrilldown, handleBackClick } =
    useDrilldown();
  const { t } = useLocalization();
  const graphColors = useGraphColors();

  if (drilledDownCategory) {
    return (
      <DrilldownView
        category={drilledDownCategory}
        fillHeight={fillDrilldownHeight}
        onBack={handleBackClick}
        groupBySubcategory
      >
        <Box>
          <Box
            display='flex'
            alignItems='center'
            mb={2}
          >
            <Button onClick={handleBackClick}>Back</Button>
            <Typography
              variant='h6'
              sx={{ ml: 2 }}
            >
              {drilledDownCategory.name}
            </Typography>
          </Box>
          <AccountsGrid
            endDate={new Date().toISOString()}
            isWidget={true}
            filteredAccounts={drilledDownCategory.accounts}
          />
        </Box>
      </DrilldownView>
    );
  }

  return (
    <DrilldownView
      category={drilledDownCategory}
      fillHeight={fillDrilldownHeight}
      onBack={handleBackClick}
      groupBySubcategory
    >
      <Box
        sx={{
          width: '100%',
          px: { xs: 0, sm: 1, md: 3 },
          py: { xs: 1, md: 3 },
        }}
      >
        <Box
          display='flex'
          justifyContent='space-around'
          sx={{
            flexDirection: stacked ? 'column' : { xs: 'column', lg: 'row' },
            alignItems: stacked ? 'center' : { xs: 'center', lg: 'flex-start' },
            gap: stacked ? 6 : { xs: 6, lg: 4 },
            // Stack the two charts down the page when printing.
            '@media print': { flexDirection: 'column', gap: 4 },
          }}
        >
          <Box
            ref={sectionRef}
            display='flex'
            flexDirection='column'
            flex={1}
            sx={{
              px: { xs: 2, lg: 0 },
              minWidth: 0,
              alignSelf: 'stretch',
              '@media print': { breakInside: 'avoid' },
            }}
          >
            <BalanceSectionHeader
              title={t('assets')}
              amount={assets.total}
            />
            <Box
              display='flex'
              flexDirection='column'
              alignItems='center'
            >
              <PieChartWithLegend<AccountCategory>
                items={assets.categories.map((c) => ({
                  label: c.name,
                  amount: c.total,
                  original: c,
                }))}
                thresholdPercent={GROUPING_THRESHOLD}
                width={pieWidth}
                height={pieHeight}
                outerRadius={pieRadius}
                seriesId='assets'
                colorResolver={(i) => graphColors[i % graphColors.length]}
                onSliceClick={(item) => {
                  if (item.original) {
                    (onDrilldown ?? handleDrilldown)(item.original);
                  }
                }}
              />
            </Box>
          </Box>

          <Box
            display='flex'
            flexDirection='column'
            flex={1}
            sx={{
              px: { xs: 2, lg: 0 },
              minWidth: 0,
              alignSelf: 'stretch',
              '@media print': { breakInside: 'avoid' },
            }}
          >
            <BalanceSectionHeader
              title={t('liabilities')}
              amount={liabilities.total}
              isNegative={true}
            />
            <Box
              display='flex'
              flexDirection='column'
              alignItems='center'
            >
              <PieChartWithLegend<AccountCategory>
                items={liabilities.categories.map((c) => ({
                  label: c.name,
                  amount: Math.abs(c.total),
                  original: c,
                }))}
                thresholdPercent={GROUPING_THRESHOLD}
                width={pieWidth}
                height={pieHeight}
                outerRadius={pieRadius}
                seriesId='liabilities'
                colorResolver={(i) => graphColors[i % graphColors.length]}
                onSliceClick={(item) => {
                  if (item.original) {
                    (onDrilldown ?? handleDrilldown)(item.original);
                  }
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </DrilldownView>
  );
}
